import React, { useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    CircularProgress,
    Snackbar,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemText,
    LinearProgress,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import axiosInstance from '../AxiosInstance';

// Type definitions
interface ImportType {
    value: string;
    label: string;
    description: string;
}

interface ValidationError {
    row: number;
    field: string;
    value: string;
    error: string;
}

interface ImportResult {
    status: 'Success' | 'Failed';
    message: string;
    totalRecords?: number;
    successfulRecords?: number;
    failedRecords?: number;
    validationErrors?: ValidationError[];
}

interface JobStatus {
    status: 'Queued' | 'Processing' | 'Completed' | 'Failed';
    message: string;
    totalRecords?: number;
    validationErrors?: ValidationError[];
}

interface ImportJob {
    jobId: string;
    status: string;
    importType: string;
    message: string;
    totalRecords?: number;
    createdUtc: string;
}

interface ImportResponse {
    jobId?: string;
    message: string;
    totalRecords?: number;
}

const ImportPage: React.FC = () => {
    const [importType, setImportType] = useState<string>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [error, setError] = useState<string>('');
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [recentJobs, setRecentJobs] = useState<ImportJob[]>([]);
    const [jobsLoading, setJobsLoading] = useState<boolean>(false);
    const [showRecentJobs, setShowRecentJobs] = useState<boolean>(false);

    const importTypes: ImportType[] = [
        { value: 'products', label: 'Products', description: 'Import product data with SKU, description, pricing, etc.' },
        { value: 'customers', label: 'Customers', description: 'Import customer data with names and channels.' },
        { value: 'salesorders', label: 'Sales Orders', description: 'Import sales order data with order lines.' },
        { value: 'retrotriggers', label: 'Retro Triggers', description: 'Import retro trigger data with customer, product, week, and trigger information.' }
    ];

    // Helper function to convert import type enum to friendly name
    const getImportTypeFriendlyName = (importType: string): string => {
        const type = importTypes.find(t => t.value === importType.toLowerCase());
        return type ? type.label : importType;
    };

    const requiredFields: Record<string, string[]> = {
        products: [
            'SkuNumber (required)',
            'SkuDescription (required)',
            'Brand (required)',
            'GroupName (required)',
            'UnitCogs (required)'
        ],
        customers: [
            'Name (required)',
            'Channel (optional)'
        ],
        salesorders: [
            'ProviderSalesOrderId (required)',
            'CustomerName (required)',
            'RequiredDateUtc (required)',
            'SkuNumber (required)',
            'OrderQuantity (required)',
            'UnitPrice (required)',
            'COGS (optional)',
            'LOGS (optional)'
        ],
        retrotriggers: [
            'CustomerName (required)',
            'SkuNumber (required)',
            'WeekStartDate (required)',
            'Units (required)',
            'TriggerValue (required)'
        ]
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const resetFileInput = (): void => {
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleFileSelect = (file: File): void => {
        if (file && file.type === 'text/csv') {
            setSelectedFile(file);
            setResult(null);
            setError('');
        } else {
            setError('Please select a valid CSV file.');
            resetFileInput();
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setIsDragOver(false);
        
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleImportTypeChange = (event: SelectChangeEvent<string>): void => {
        setImportType(event.target.value);
        setSelectedFile(null);
        setResult(null);
        setError('');
        resetFileInput();
    };

    const handleImport = async (): Promise<void> => {
        if (!importType || !selectedFile) {
            setError('Please select an import type and file.');
            return;
        }

        // Clear all previous states
        setError('');
        setResult(null);
        setJobStatus(null);
        stopPolling();
        
        setLoading(true);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await axiosInstance.post<ImportResponse>(`/import/${importType}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            // If we get a job ID, start polling for status
            if (response.data.jobId) {
                setJobStatus({
                    status: 'Queued',
                    message: response.data.message,
                    totalRecords: response.data.totalRecords
                });
                
                // Add the new job to the recent jobs list (don't auto-expand)
                const newJob: ImportJob = {
                    jobId: response.data.jobId,
                    status: 'Queued',
                    importType: importType.charAt(0).toUpperCase() + importType.slice(1),
                    message: response.data.message,
                    totalRecords: response.data.totalRecords,
                    createdUtc: new Date().toISOString()
                };
                setRecentJobs(prevJobs => [newJob, ...prevJobs.slice(0, 4)]);
                
                startPolling(response.data.jobId);
            } else {
                // Handle old-style response or error
                setResult(response.data as ImportResult);
                setSnackbarOpen(true);
            }
        } catch (err: any) {
            if (err.response?.data?.validationErrors) {
                setResult(err.response.data);
                // Reset file input so user can re-select the same file after fixing errors
                resetFileInput();
                setSelectedFile(null);
            }
            else {
                const errorMessage = err.response?.data?.message || err.response?.data || 'An error occurred during import.';
                setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (): void => {
        setSnackbarOpen(false);
    };

    const resetForm = (): void => {
        setImportType('');
        setSelectedFile(null);
        setResult(null);
        setError('');
        setJobStatus(null);
        resetFileInput();
        stopPolling();
    };

    const startPolling = (jobId: string): void => {
        // Clear any existing polling
        stopPolling();
        
        // Start polling every 2 seconds
        const interval = setInterval(async () => {
            try {
                const response = await axiosInstance.get<JobStatus>(`/import/status/${jobId}`);
                const status = response.data;
                
                setJobStatus(status);
                
                // Update the recent jobs list to reflect current status
                setRecentJobs(prevJobs => 
                    prevJobs.map(job => 
                        job.jobId === jobId 
                            ? { ...job, status: status.status, message: status.message }
                            : job
                    )
                );
                
                // Stop polling if job is completed or failed
                if (status.status === 'Completed' || status.status === 'Failed') {
                    // Clear the interval immediately
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }
                    
                    // Clear any previous error state
                    setError('');
                    
                    setResult({
                        status: status.status === 'Completed' ? 'Success' : 'Failed',
                        message: status.message,
                        totalRecords: status.totalRecords,
                        successfulRecords: status.status === 'Completed' ? status.totalRecords : 0,
                        failedRecords: status.status === 'Failed' ? status.totalRecords : 0,
                        validationErrors: status.validationErrors
                    });
                    setSnackbarOpen(true);
                }
            } catch (err) {
                console.error('Error polling job status:', err);
                setError('Error checking import status. Please refresh the page.');
                // Clear the interval immediately
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            }
        }, 2000);
        
        pollingIntervalRef.current = interval;
    };
    
    const stopPolling = (): void => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };
    
    const fetchRecentJobs = async (): Promise<void> => {
        try {
            setJobsLoading(true);
            const response = await axiosInstance.get<ImportJob[]>('/import/jobs');
            
            // Filter jobs from the last 30 days and get the 5 most recent
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            
            const recentJobsFiltered = response.data
                .filter(job => new Date(job.createdUtc) >= thirtyDaysAgo)
                .slice(0, 5); // Show only the 5 most recent
                
            setRecentJobs(recentJobsFiltered);
        } catch (err) {
            console.error('Error fetching recent jobs:', err);
        } finally {
            setJobsLoading(false);
        }
    };

    // Fetch recent jobs on component mount and cleanup polling on unmount
    React.useEffect(() => {
        fetchRecentJobs();
        return () => {
            stopPolling();
        };
    }, []);

    // Refresh recent jobs when current job completes
    React.useEffect(() => {
        if (result && ['Success', 'Failed'].includes(result.status)) {
            fetchRecentJobs();
        }
    }, [result]);

    const handleDownloadExample = async (): Promise<void> => {
        try {
            const response = await axiosInstance.get(`/import/example/${importType}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `example_${importType}_import.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Failed to download example CSV file.');
        }
    };

    return (
        <Box className="component-container" sx={{ width: 800, margin: '0 auto', padding: 3 }}>
            <Typography variant="h4" gutterBottom>
                Import
            </Typography>
            
            <Typography variant="body1" color="textSecondary" marginBottom={2}>
                Import data from CSV files. Select the type of data you want to import and upload your CSV file.
            </Typography>

            {/* Recent Import Jobs Section - Only show if there are jobs */}
            {recentJobs.length > 0 && (
                <Accordion
                    expanded={showRecentJobs}
                    onChange={(event, isExpanded) => setShowRecentJobs(isExpanded)}
                    sx={{ mb: 3 }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="recent-jobs-content"
                        id="recent-jobs-header"
                    >
                        <Typography variant="h6">
                            Recent Imports
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {jobsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <List dense>
                                {recentJobs.map((job) => (
                                    <ListItem key={job.jobId} sx={{ px: 0, py: 1 }}>
                                        <Card variant="outlined" sx={{ width: '100%', bgcolor: 'background.paper' }}>
                                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '120px' }}>
                                                            {getImportTypeFriendlyName(job.importType)}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: job.status === 'Completed' ? 'success.main' :
                                                                       job.status === 'Failed' ? 'error.main' :
                                                                       job.status === 'Processing' ? 'warning.main' : 'text.secondary',
                                                                fontWeight: 'medium',
                                                                fontSize: '0.8rem'
                                                            }}
                                                        >
                                                            {job.status}
                                                        </Typography>
                                                        {job.status === 'Processing' && (
                                                            <CircularProgress size={14} />
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                        {new Date(job.createdUtc).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(job.createdUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                    {job.message}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" marginBottom={2}>
                        What type of data do you want to import?
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Import Type</InputLabel>
                        <Select
                            value={importType}
                            onChange={handleImportTypeChange}
                            label="Import Type"
                        >
                            {importTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    <Box>
                                        <Typography variant="body1" component="div">{type.label}</Typography>
                                        <Typography variant="body2" color="textSecondary" component="div">
                                            {type.description}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {importType && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                CSV Format for {importTypes.find(t => t.value === importType)?.label}
                            </Typography>
                            <Typography variant="body1" color="textSecondary" marginBottom={2}>
                                Your CSV file must have a header row with the following column names in any order.
                            </Typography>
                            <Typography variant="body1" color="textSecondary" marginBottom={2}>
                                Required columns:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {requiredFields[importType]?.filter(field => field.includes('required')).map((field, index) => (
                                    <Chip
                                        key={index}
                                        label={field.replace(' (required)', '')}
                                        size="small"
                                        color="primary"
                                    />
                                ))}
                            </Box>
                            {(() => {
                                const optionalFields = requiredFields[importType]?.filter(field => field.includes('optional'));
                                return optionalFields.length > 0 ? (
                                    <>
                                        <Typography variant="body1" color="textSecondary" marginBottom={2}>
                                            Optional columns:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            {optionalFields.map((field, index) => (
                                                <Chip
                                                    key={index}
                                                    label={field.replace(' (optional)', '')}
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    </>
                                ) : null;
                            })()}
                            
                            <Button
                                variant="outlined"
                                startIcon={<DescriptionIcon />}
                                onClick={handleDownloadExample}
                                sx={{ mt: 1 }}
                            >
                                Download Example CSV
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {importType && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Upload CSV File
                        </Typography>
                        
                        <Box
                            sx={{
                            border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                            borderRadius: 2,
                            padding: 4,
                            textAlign: 'center',
                            mb: 2,
                            cursor: 'pointer',
                            backgroundColor: isDragOver ? 'action.hover' : 'transparent',
                            transition: 'border-color 0.2s, background-color 0.2s',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover',
                            },
                        }}
                        onClick={() => document.getElementById('file-input')?.click()}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        >
                            <input
                                id="file-input"
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                                {selectedFile ? selectedFile.name : 'Click to select or drag & drop a CSV file'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Only .csv files are supported
                            </Typography>
                        </Box>

                        {selectedFile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <DescriptionIcon sx={{ mr: 1 }} />
                                <Typography variant="body2">
                                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                                </Typography>
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            onClick={handleImport}
                            disabled={!importType || !selectedFile || loading || (jobStatus && ['Queued', 'Processing'].includes(jobStatus.status)) || pollingIntervalRef.current !== null}
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    Validating...
                                </>
                            ) : jobStatus && ['Queued', 'Processing'].includes(jobStatus.status) ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    {jobStatus.status}...
                                </>
                            ) : (
                                'Import CSV'
                            )}
                        </Button>
                    </CardContent>
                </Card>
            )}

            {(loading || jobStatus) && !result && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {loading
                                ? 'Validating...'
                                : jobStatus?.status ?? 'Import Status'}
                        </Typography>
                        {(loading || jobStatus && (jobStatus.status === 'Queued' || jobStatus.status === 'Processing')) && <LinearProgress />}
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {loading
                                ? 'Please wait...'
                                : jobStatus?.status === 'Queued'
                                    ? 'Your import will begin shortly.'
                                    : jobStatus?.status === 'Processing'
                                        ? 'Your import is being processed.'
                                        : jobStatus?.message || 'Waiting for status update...'}
                        </Typography>
                        {jobStatus && (
                            <Box sx={{ mt: 2 }}>
                                {jobStatus.status === 'Completed' && (
                                    <Typography variant="body2" color="success.main">
                                        <strong>Status:</strong> Completed
                                    </Typography>
                                )}
                                {jobStatus.status === 'Failed' && (
                                    <Typography variant="body2" color="error.main">
                                        <strong>Status:</strong> Failed
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {result && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {result.status === 'Success' ? (
                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                            ) : (
                                <ErrorIcon color="error" sx={{ mr: 1 }} />
                            )}
                            <Typography variant="h6">
                                Import {result.status === 'Success' ? 'Completed' : 'Failed'}
                            </Typography>
                        </Box>

                        <Typography variant="body1" marginBottom={2}>
                            {result.message}
                        </Typography>

                        {result.validationErrors && result.validationErrors.length > 0 && (
                            <>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography variant="body1" gutterBottom component="div">
                                            <strong>No data was imported</strong> because of validation errors.
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            Please fix the errors in your CSV file and select the file again to upload.
                                        </Typography>
                                    </Box>
                                </Alert>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <Chip label={`Total lines: ${result.totalRecords}`} color="default" />
                                    <Chip 
                                        label={`Valid lines: ${(result.totalRecords || 0) - (result.failedRecords || 0)}`} 
                                        color="success" 
                                        variant="outlined" 
                                    />
                                    <Chip label={`Invalid lines: ${result.failedRecords}`} color="error" />
                                </Box>
                            </>
                        )}

                        {result.validationErrors && result.validationErrors.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Validation Errors
                                </Typography>
                                <List dense>
                                    {result.validationErrors.slice(0, 10).map((error, index) => (
                                        <ListItem key={index}>
                                            <Box>
                                                <Typography variant="body2" component="div">
                                                    Row {error.row}: {error.field}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" component="div">
                                                    Value: "{error.value}" - {error.error}
                                                </Typography>
                                            </Box>
                                        </ListItem>
                                    ))}
                                    {result.validationErrors.length > 10 && (
                                        <ListItem>
                                            <Typography variant="body2" color="textSecondary" component="div">
                                                ... and {result.validationErrors.length - 10} more errors
                                            </Typography>
                                        </ListItem>
                                    )}
                                </List>
                            </>
                        )}

                        {result.status === 'Success' && (
                            <Button
                                variant="outlined"
                                onClick={resetForm}
                                sx={{ mt: 2 }}
                            >
                                Import Another File
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={result?.status === 'Success' ? 'success' : 'error'}>
                    {result?.status === 'Success' 
                        ? `Import completed successfully.`
                        : 'Import failed. Please check the errors above.'
                    }
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ImportPage; 